"use client";

import { useCallback, useEffect, useState } from "react";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus } from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import {
  fetchBookableResources,
  fetchDayAvailability,
  saveReservation,
  type BookableResource,
  type DayAvailability,
  type ReservationSlot,
} from "@/lib/api/c-resource-reservation-user";
import styles from "./CResourceReservation.module.css";

/**
 * Booking nowej rezerwacji (slot picker, BEZ Syncfusion).
 *
 * Krok 1: lista zasobow (karty) -> wybor zasobu.
 * Krok 2: wybor dnia (date input) -> backend zwraca sloty -> klik wolnego slotu ->
 *         "Zarezerwuj" -> POST z autorytatywna walidacja po stronie backendu.
 *
 * Po udanej rezerwacji wola `onBooked` (parent przelacza na liste rezerwacji + reload).
 */

/** Lokalna data YYYY-MM-DD (dzis) - do min date input i wartosci domyslnej. */
function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function ResourceBooking({ onBooked }: { onBooked: () => void }) {
  const [resources, setResources] = useState<BookableResource[]>([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [resourcesError, setResourcesError] = useState<string | null>(null);

  const [selected, setSelected] = useState<BookableResource | null>(null);
  const [date, setDate] = useState<string>(todayIso());

  const [availability, setAvailability] = useState<DayAvailability | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ReservationSlot | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingResources(true);
    setResourcesError(null);
    (async () => {
      try {
        const { resources: list } = await fetchBookableResources();
        if (cancelled) return;
        setResources(list);
      } catch (e) {
        if (cancelled) return;
        setResourcesError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoadingResources(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSlots = useCallback(async (resourceId: number, day: string) => {
    setLoadingSlots(true);
    setSlotsError(null);
    setSelectedSlot(null);
    try {
      const data = await fetchDayAvailability(resourceId, day);
      setAvailability(data);
    } catch (e) {
      setAvailability(null);
      setSlotsError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selected) void loadSlots(selected.id, date);
  }, [selected, date, loadSlots]);

  const doReserve = useCallback(async () => {
    if (!selected || !selectedSlot) return;
    setSaving(true);
    try {
      await saveReservation(selected.id, selectedSlot.start, selectedSlot.end);
      toast.success("Rezerwacja zapisana.");
      onBooked();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Nie udało się zapisać rezerwacji.");
      /* Odswiez sloty - termin mogl zostac zajety w miedzyczasie. */
      if (selected) void loadSlots(selected.id, date);
    } finally {
      setSaving(false);
    }
  }, [selected, selectedSlot, date, onBooked, loadSlots]);

  /* ── Krok 1: lista zasobow ── */
  if (!selected) {
    return (
      <div className={styles.root}>
        <p className={styles.note}>Wybierz zasób, który chcesz zarezerwować.</p>

        {loadingResources && <FormStatus variant="info">Wczytywanie zasobów...</FormStatus>}
        {resourcesError && !loadingResources && (
          <FormStatus variant="error">{resourcesError}</FormStatus>
        )}
        {!loadingResources && !resourcesError && resources.length === 0 && (
          <FormStatus variant="info">Brak zasobów do rezerwacji.</FormStatus>
        )}

        {!loadingResources && resources.length > 0 && (
          <div className={styles.resourceGrid}>
            {resources.map((r) => (
              <div key={r.id} className={styles.resourceCard}>
                <h3 className={styles.resourceCardName}>{r.name}</h3>
                {r.description && (
                  <div
                    className={styles.resourceCardDesc}
                    dangerouslySetInnerHTML={{ __html: r.description }}
                  />
                )}
                {r.max_people > 0 && (
                  <span className={styles.resourceCardMeta}>Maks. osób: {r.max_people}</span>
                )}
                <button
                  type="button"
                  className={styles.selectBtn}
                  onClick={() => setSelected(r)}
                >
                  <Icon name="calendar" className={styles.actionIcon} />
                  Wybierz termin
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Krok 2: slot picker ── */
  return (
    <div className={styles.root}>
      <div className={styles.bookHeader}>
        <h3 className={styles.bookTitle}>{selected.name}</h3>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => {
            setSelected(null);
            setAvailability(null);
            setSelectedSlot(null);
          }}
        >
          <Icon name="caret-left" />
          Zmień zasób
        </button>
      </div>

      <div className={styles.dateRow}>
        <label htmlFor="reservation-date" className={styles.dateLabel}>
          Dzień:
        </label>
        <input
          id="reservation-date"
          type="date"
          className={styles.dateInput}
          value={date}
          min={todayIso()}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {loadingSlots && <FormStatus variant="info">Wczytywanie terminów...</FormStatus>}
      {slotsError && !loadingSlots && <FormStatus variant="error">{slotsError}</FormStatus>}

      {!loadingSlots && !slotsError && availability && availability.slots.length === 0 && (
        <FormStatus variant="info">Brak dostępnych terminów w tym dniu.</FormStatus>
      )}

      {!loadingSlots && availability && availability.slots.length > 0 && (
        <>
          <div className={styles.slotGrid}>
            {availability.slots.map((slot) => {
              const isSelected = selectedSlot?.start === slot.start;
              return (
                <button
                  key={slot.start}
                  type="button"
                  className={`${styles.slot} ${slot.taken ? styles.slotTaken : ""} ${
                    isSelected ? styles.slotSelected : ""
                  }`}
                  disabled={slot.taken}
                  onClick={() => setSelectedSlot(slot)}
                  aria-pressed={isSelected}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>

          <div className={styles.bookActions}>
            <button
              type="button"
              className={styles.reserveBtn}
              onClick={() => void doReserve()}
              disabled={!selectedSlot || saving}
            >
              <Icon name="check" className={styles.actionIcon} />
              {selectedSlot ? `Zarezerwuj ${selectedSlot.label}` : "Wybierz termin"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
